// src/utils/roles.ts — SCE Issues
// Les rôles gérés depuis le panneau d'administration (22/10, maquette
// https://claude.ai/artifact/Pz1B7r3ZUzA9gqKJS5uegT) : coordinateurs et
// suppléants, BE de chaque métier, responsables des sujets (Goulotte, 23/10),
// rôles FNC-T, externes et leurs sociétés (29/10), appareils partagés, comptes
// secondaires, ouvertures.
// Pur : ni React, ni réseau.
//
// Rangement : bloc `roles` de la ligne `__global__` de SCE_UserPrefs (comme
// les couleurs métier). Les listes écrites dans le code restent les valeurs PAR
// DÉFAUT (config/rolesDefaut) : un bloc absent, ou une clé absente du bloc, et
// l'appli fait exactement comme avant. Les flows 1 et 3 lisent la même liste
// de coordinateurs (docs/flows/LISEZMOI.md).
//
// ⚠️ Les ADMINISTRATEURS restent dans le code (config/admin.config) : la ligne
//    `__global__` est modifiable par tout le site, un administrateur écrit là
//    pourrait se nommer lui-même.

import { SUJETS, type Metier, type Sujet } from '@/types/issue.types'
import { sha256Hex } from '@/utils/empreinte'
import { LIBELLE_MONTAGE, METIERS_MONTAGE, type MetierMontage } from '@/utils/montages'

export const CLE_BLOC_ROLES = 'roles'
export const METIERS_ROLES: readonly Metier[] = ['ELEC', 'AUTOM', 'MECA', 'PROCESS']

/** Une personne de l'annuaire : « Prénom Nom » et son adresse (UPN), en minuscules. */
export interface PersonneRole {
  nom: string
  upn: string
}

export interface CoordinationMetier {
  /** Inscrits sur la fiche, reçoivent l'alerte. */
  titulaires: PersonneRole[]
  /** Mêmes droits, en copie. */
  suppleants: PersonneRole[]
}

/** Un invité B2B : l'annuaire ne le montre pas, on le tient ici. */
export interface ExterneRole {
  prenom: string
  nom: string
  /** L'adresse où il reçoit ses mails (pas l'identifiant « #EXT# »). */
  email: string
  societe: string
  /** Date ISO de la demande d'ajout au groupe SEC_GLOB_OP_SCE ; null : pas encore faite. */
  demandeIT: string | null
  ajoutePar: string
  le: string
}

/**
 * Une société extérieure (29/10) : les sous-traitants du montage Élec et Méca,
 * pour commencer. Ses externes s'y rattachent par le NOM (champ `societe` de
 * l'externe, sans casse ni accents) : un externe écrit avant le 29/10 avec la
 * même société s'y range seul. Prépare une vue limitée aux équipements dont la
 * société a la charge — lisibilité, pas sécurité : rien de sensible (pas de prix).
 */
export interface SocieteRole {
  nom: string
  /** Domaines mail de ses externes (« actemium.com ») : proposent la société d'un nouvel externe. */
  domaines: string[]
  /** Les montages qu'elle fait pour l'usine : Élec, Méca (pas de montage Autom ni Process). */
  montages: MetierMontage[]
  /** Identifiant du fichier de son logo (api/logosSocietes) ; absent : pas de logo. */
  logo?: string
  ajoutePar: string
  le: string
}

/** Un appareil commun à plusieurs personnes (iPad de l'atelier), sous un compte invité. */
export interface AppareilRole {
  /** Object ID Entra du compte de l'appareil. */
  objectId: string
  /** SHA-256 de l'adresse du compte (relecture de l'auteur quand Graph ne donne pas l'Object ID). */
  empreinteAdresse?: string
  libelle: string
  personnes: PersonneRole[]
}

/** Un compte invité qui représente une seule personne (téléphone personnel). */
export interface SecondaireRole {
  objectId: string
  empreinteAdresse?: string
  appareil: string
  personne: PersonneRole
}

export interface RolesFnct {
  testeurs: PersonneRole[]
  aqcq: PersonneRole[]
  revue: PersonneRole[]
}

/**
 * Un chef de projet (22/10) : le nom vient de Windchill (responsable du PN, lu
 * dans le référentiel) ; l'administrateur fixe son compte dans l'annuaire, ou
 * ajoute quelqu'un qui n'y est pas encore. Pas de droits pour l'instant : une
 * vue de ses projets viendra.
 */
export interface ChefProjetRole {
  /** « Prénom Nom », tel que le référentiel l'affiche. */
  nom: string
  /** Son compte, choisi dans le panneau ; null : à retrouver dans l'annuaire. */
  upn: string | null
  /** Ajouté à la main, absent du référentiel. */
  horsWindchill: boolean
}

export interface Ouvertures {
  /** « Envoyer au BE » ouvert aux coordinateurs (sinon grisé « En cours », admin excepté). */
  envoiBE: boolean
  /** Le choix d'un sujet (Goulotte) à la déclaration, ouvert à tous (sinon administrateur seul). */
  sujets: boolean
  /** Accueil personnalisable ouvert à tous (sinon administrateur seul : essai du 03/11). */
  accueilPerso: boolean
}

export interface Roles {
  version: 1
  coordinateurs: Record<Metier, CoordinationMetier>
  be: Record<Metier, PersonneRole[]>
  /** Responsables de chaque sujet : alertés et pilotes de ses problèmes (config/sujets.config). */
  sujets: Record<Sujet, PersonneRole[]>
  fnct: RolesFnct
  externes: ExterneRole[]
  societes: SocieteRole[]
  appareils: AppareilRole[]
  secondaires: SecondaireRole[]
  chefsProjet: ChefProjetRole[]
  ouvertures: Ouvertures
}

// ─── Lecture tolérante ───

const texte = (v: unknown): string => (typeof v === 'string' ? v.trim() : '')
const adresse = (v: unknown): string => texte(v).toLowerCase()
const estObjet = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)

/** Une personne lisible (adresse avec « @ »), sinon null. */
export function lirePersonne(v: unknown): PersonneRole | null {
  if (!estObjet(v)) return null
  const upn = adresse(v.upn)
  if (!upn.includes('@')) return null
  return { nom: texte(v.nom) || upn, upn }
}

/** Une liste de personnes, sans doublon d'adresse ; null si ce n'est pas une liste. */
export function lirePersonnes(v: unknown): PersonneRole[] | null {
  if (!Array.isArray(v)) return null
  const vues = new Set<string>()
  const res: PersonneRole[] = []
  for (const x of v) {
    const p = lirePersonne(x)
    if (p && !vues.has(p.upn)) {
      vues.add(p.upn)
      res.push(p)
    }
  }
  return res
}

function lireExterne(v: unknown): ExterneRole | null {
  if (!estObjet(v)) return null
  const email = adresse(v.email)
  if (!email.includes('@') || email.includes('#ext#')) return null
  return {
    prenom: texte(v.prenom),
    nom: texte(v.nom),
    email,
    societe: texte(v.societe),
    demandeIT: texte(v.demandeIT) || null,
    ajoutePar: texte(v.ajoutePar),
    le: texte(v.le),
  }
}

function lireSociete(v: unknown): SocieteRole | null {
  if (!estObjet(v)) return null
  const nom = texte(v.nom)
  if (!nom) return null
  const domaines = Array.isArray(v.domaines) ? [...new Set(v.domaines.map(domaineNormalise).filter(Boolean))] : []
  // Élec et Méca seulement : il n'y a pas de montage Autom ni Process (Guillaume, 29/10).
  const montages = Array.isArray(v.montages) ? METIERS_MONTAGE.filter((m) => (v.montages as unknown[]).includes(m)) : []
  const logo = texte(v.logo)
  return { nom, domaines, montages, ...(logo ? { logo } : {}), ajoutePar: texte(v.ajoutePar), le: texte(v.le) }
}

function lireAppareil(v: unknown): AppareilRole | null {
  if (!estObjet(v)) return null
  const objectId = adresse(v.objectId)
  const libelle = texte(v.libelle)
  if (!objectId || !libelle) return null
  const empreinte = adresse(v.empreinteAdresse)
  return { objectId, libelle, personnes: lirePersonnes(v.personnes) ?? [], ...(empreinte ? { empreinteAdresse: empreinte } : {}) }
}

function lireSecondaire(v: unknown): SecondaireRole | null {
  if (!estObjet(v)) return null
  const objectId = adresse(v.objectId)
  const personne = lirePersonne(v.personne)
  if (!objectId || !personne) return null
  const empreinte = adresse(v.empreinteAdresse)
  return { objectId, appareil: texte(v.appareil) || 'Appareil personnel', personne, ...(empreinte ? { empreinteAdresse: empreinte } : {}) }
}

function lireChef(v: unknown): ChefProjetRole | null {
  if (!estObjet(v)) return null
  const nom = texte(v.nom)
  if (!nom) return null
  const upn = adresse(v.upn)
  return { nom, upn: upn.includes('@') ? upn : null, horsWindchill: v.horsWindchill === true }
}

/** Une liste d'éléments lus un à un (écartés s'ils sont abîmés) ; null si ce n'est pas une liste. */
function lireListe<T>(v: unknown, lire: (x: unknown) => T | null, cle: (x: T) => string): T[] | null {
  if (!Array.isArray(v)) return null
  const vues = new Set<string>()
  const res: T[] = []
  for (const x of v) {
    const e = lire(x)
    if (e && !vues.has(cle(e))) {
      vues.add(cle(e))
      res.push(e)
    }
  }
  return res
}

/**
 * Le bloc `roles` relu, complété par les valeurs par défaut : une clé absente
 * ou illisible garde la valeur du code. Jamais d'exception.
 */
export function lireRoles(brut: unknown, defaut: Roles): Roles {
  const b = estObjet(brut) ? brut : {}
  const coordBrut = estObjet(b.coordinateurs) ? b.coordinateurs : {}
  const beBrut = estObjet(b.be) ? b.be : {}
  const sujetsBrut = estObjet(b.sujets) ? b.sujets : {}
  const fnctBrut = estObjet(b.fnct) ? b.fnct : {}
  const ouvBrut = estObjet(b.ouvertures) ? b.ouvertures : {}

  const coordinateurs = {} as Record<Metier, CoordinationMetier>
  const be = {} as Record<Metier, PersonneRole[]>
  for (const m of METIERS_ROLES) {
    const c = estObjet(coordBrut[m]) ? coordBrut[m] : null
    coordinateurs[m] = c
      ? {
          titulaires: lirePersonnes(c.titulaires) ?? defaut.coordinateurs[m].titulaires,
          suppleants: lirePersonnes(c.suppleants) ?? defaut.coordinateurs[m].suppleants,
        }
      : defaut.coordinateurs[m]
    be[m] = lirePersonnes(beBrut[m]) ?? defaut.be[m]
  }

  return {
    version: 1,
    coordinateurs,
    be,
    sujets: Object.fromEntries(SUJETS.map((s) => [s, lirePersonnes(sujetsBrut[s]) ?? defaut.sujets[s]])) as Record<Sujet, PersonneRole[]>,
    fnct: {
      testeurs: lirePersonnes(fnctBrut.testeurs) ?? defaut.fnct.testeurs,
      aqcq: lirePersonnes(fnctBrut.aqcq) ?? defaut.fnct.aqcq,
      revue: lirePersonnes(fnctBrut.revue) ?? defaut.fnct.revue,
    },
    externes: lireListe(b.externes, lireExterne, (e) => e.email) ?? defaut.externes,
    societes: lireListe(b.societes, lireSociete, (s) => cleNom(s.nom)) ?? defaut.societes,
    appareils: lireListe(b.appareils, lireAppareil, (a) => a.objectId) ?? defaut.appareils,
    secondaires: lireListe(b.secondaires, lireSecondaire, (s) => s.objectId) ?? defaut.secondaires,
    chefsProjet: lireListe(b.chefsProjet, lireChef, (c) => cleNom(c.nom)) ?? defaut.chefsProjet,
    ouvertures: {
      envoiBE: typeof ouvBrut.envoiBE === 'boolean' ? ouvBrut.envoiBE : defaut.ouvertures.envoiBE,
      sujets: typeof ouvBrut.sujets === 'boolean' ? ouvBrut.sujets : defaut.ouvertures.sujets,
      accueilPerso: typeof ouvBrut.accueilPerso === 'boolean' ? ouvBrut.accueilPerso : defaut.ouvertures.accueilPerso,
    },
  }
}

// ─── Gestes de l'administrateur ───

/** Clé d'un nom : casse, accents et espaces ignorés (« Lucile  Goy » = « lucile goy »). */
export function cleNom(nom: string): string {
  return nom.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/\s+/g, ' ').trim()
}

export interface LigneChefProjet {
  nom: string
  /** Nombre de PN dont il est responsable dans le référentiel. */
  projets: number
  /** Compte fixé dans le panneau, sinon null. */
  upn: string | null
  horsWindchill: boolean
}

/**
 * Les chefs de projet du panneau : ceux du référentiel (un par nom, avec leur
 * nombre de PN) et ceux ajoutés à la main, chacun avec le compte enregistré.
 */
export function lignesChefsProjet(
  equipements: readonly { chefProjet?: string; pn?: string }[],
  enregistres: readonly ChefProjetRole[],
): LigneChefProjet[] {
  const parCle = new Map<string, { nom: string; pns: Set<string> }>()
  for (const e of equipements) {
    const nom = (e.chefProjet ?? '').trim()
    if (!nom) continue
    const cle = cleNom(nom)
    const ligne = parCle.get(cle) ?? { nom, pns: new Set<string>() }
    if (e.pn?.trim()) ligne.pns.add(e.pn.trim().toUpperCase())
    parCle.set(cle, ligne)
  }
  const lignes: LigneChefProjet[] = [...parCle.entries()].map(([cle, l]) => {
    const r = enregistres.find((c) => cleNom(c.nom) === cle)
    return { nom: l.nom, projets: l.pns.size, upn: r?.upn ?? null, horsWindchill: false }
  })
  for (const c of enregistres) {
    if (c.horsWindchill && !parCle.has(cleNom(c.nom))) lignes.push({ nom: c.nom, projets: 0, upn: c.upn, horsWindchill: true })
  }
  return lignes.sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
}

/** Fixe le compte d'un chef de projet (créé s'il n'était pas encore enregistré). */
export function avecCompteChef(liste: readonly ChefProjetRole[], nom: string, upn: string, horsWindchill = false): ChefProjetRole[] {
  const cle = cleNom(nom)
  const existant = liste.find((c) => cleNom(c.nom) === cle)
  const chef: ChefProjetRole = { nom: existant?.nom ?? nom.trim(), upn: upn.trim().toLowerCase(), horsWindchill: existant?.horsWindchill ?? horsWindchill }
  return [...liste.filter((c) => cleNom(c.nom) !== cle), chef]
}

/** Retire un chef de projet ajouté à la main. */
export function sansChef(liste: readonly ChefProjetRole[], nom: string): ChefProjetRole[] {
  return liste.filter((c) => cleNom(c.nom) !== cleNom(nom))
}

const memeUpn = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase()

/** La liste avec cette personne en plus (à la fin), sans doublon. */
export function avecPersonne(liste: readonly PersonneRole[], p: PersonneRole): PersonneRole[] {
  const upn = p.upn.trim().toLowerCase()
  if (!upn.includes('@') || liste.some((x) => memeUpn(x.upn, upn))) return [...liste]
  return [...liste, { nom: p.nom.trim() || upn, upn }]
}

/** La liste sans cette personne. */
export function sansPersonne(liste: readonly PersonneRole[], upn: string): PersonneRole[] {
  return liste.filter((x) => !memeUpn(x.upn, upn))
}

/** « Charles Louis » : le nom affiché d'un externe. */
export function nomExterne(e: Pick<ExterneRole, 'prenom' | 'nom'>): string {
  return [e.prenom, e.nom].map((x) => x.trim()).filter(Boolean).join(' ')
}

/**
 * Société proposée d'après l'adresse : « charles.louis@vinci-energies.net » →
 * « Vinci Energies ». Une proposition, que l'administrateur corrige.
 */
export function societeProposee(email: string): string {
  const domaine = email.trim().toLowerCase().split('@')[1] ?? ''
  const nom = domaine.split('.')[0] ?? ''
  return nom
    .split(/[-_]/)
    .filter(Boolean)
    .map((m) => m.charAt(0).toUpperCase() + m.slice(1))
    .join(' ')
}

/** Ce qui manque pour enregistrer un externe ; null si tout va bien. */
export function erreurExterne(e: Pick<ExterneRole, 'prenom' | 'nom' | 'email'>, existants: readonly ExterneRole[]): string | null {
  if (!e.prenom.trim() || !e.nom.trim()) return 'Le prénom et le nom sont obligatoires.'
  const email = e.email.trim().toLowerCase()
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'L’adresse mail n’est pas valide.'
  if (email.includes('#ext#')) return 'Il faut l’adresse où il reçoit ses mails, pas l’identifiant « #EXT# ».'
  if (email.endsWith('@sartorius.com')) return 'Une adresse Sartorius est dans l’annuaire : pas besoin de l’ajouter ici.'
  if (existants.some((x) => x.email === email)) return 'Cet externe est déjà dans la liste.'
  return null
}

/** Le texte du mail à l'IT pour ajouter un externe au groupe de sécurité. */
export const GROUPE_SECURITE = 'SEC_GLOB_OP_SCE'
export const CONTACT_IT = 'malte.strunk@sartorius.com'

/** En anglais (Guillaume, 22/10) : l'IT est un service du groupe. */
export function lienMailIT(email: string, nom: string): string {
  const sujet = `Request: add an external user to ${GROUPE_SECURITE}`
  const corps =
    `Hello,\n\nCould you please add ${email.trim().toLowerCase()} (${nom}) to the security group ${GROUPE_SECURITE}?` +
    `\nThis external user needs to open the SCE Issues records we send them (Sartorius Chromatography Equipment, Pompey).\n\nThank you`
  return `mailto:${CONTACT_IT}?subject=${encodeURIComponent(sujet)}&body=${encodeURIComponent(corps)}`
}

/**
 * Une adresse qu'on peut proposer comme destinataire : un compte Sartorius, ou
 * un externe du panneau. Écarte les comptes personnels (invités du tenant :
 * téléphone, ordinateur, iPad) restés dans « Vos envois fréquents » (22/10).
 */
export function adresseProposable(upn: string, externes: readonly Pick<ExterneRole, 'email'>[]): boolean {
  const a = upn.trim().toLowerCase()
  if (a.includes('#ext#')) return false
  return a.endsWith('@sartorius.com') || externes.some((e) => e.email === a)
}

/** Les externes qui correspondent à une recherche (prénom, nom, société, adresse). */
export function rechercherExternes(externes: readonly ExterneRole[], q: string): ExterneRole[] {
  const mots = q.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (mots.length === 0) return []
  const sansAccent = (s: string): string => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()
  return externes.filter((e) => {
    const botte = sansAccent(`${e.prenom} ${e.nom} ${e.societe} ${e.email}`)
    return mots.every((m) => botte.includes(sansAccent(m)))
  })
}

// ─── Sociétés (29/10) ───

/** Messageries publiques : jamais le domaine d'une société (un sous-traitant en Gmail). */
const DOMAINES_PUBLICS = new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'outlook.fr', 'hotmail.com', 'hotmail.fr', 'live.com', 'live.fr',
  'msn.com', 'yahoo.com', 'yahoo.fr', 'icloud.com', 'me.com', 'orange.fr', 'wanadoo.fr', 'free.fr', 'sfr.fr',
  'laposte.net', 'bbox.fr', 'neuf.fr', 'aol.com', 'proton.me', 'protonmail.com',
])

function domaineNormalise(v: unknown): string {
  return texte(v).toLowerCase().replace(/^@/, '')
}

/** Le domaine d'une adresse (« actemium.com »), vide pour une messagerie publique. */
export function domaineSociete(email: string): string {
  const d = email.trim().toLowerCase().split('@')[1] ?? ''
  return DOMAINES_PUBLICS.has(d) ? '' : d
}

/** « Actemium, actemium.fr ; vinci-energies.net » → les domaines, nettoyés et sans doublon. */
export function lireDomaines(saisie: string): string[] {
  return [...new Set(saisie.split(/[\s,;]+/).map(domaineNormalise).filter((d) => d.includes('.')))]
}

/** La société de la liste qui porte ce nom (sans casse ni accents), ou null. */
export function societeNommee(nom: string, societes: readonly SocieteRole[]): SocieteRole | null {
  const cle = cleNom(nom)
  return cle ? societes.find((s) => cleNom(s.nom) === cle) ?? null : null
}

/** La société d'un externe, si son nom est dans la liste. */
export function societeDeLExterne(e: Pick<ExterneRole, 'societe'>, societes: readonly SocieteRole[]): SocieteRole | null {
  return societeNommee(e.societe, societes)
}

/** La société dont un domaine (ou un domaine parent) est celui de l'adresse. */
export function societeDuDomaine(email: string, societes: readonly SocieteRole[]): SocieteRole | null {
  const d = domaineSociete(email)
  if (!d) return null
  return societes.find((s) => s.domaines.some((x) => d === x || d.endsWith(`.${x}`))) ?? null
}

/** Ce qui manque pour enregistrer une société ; null si tout va bien. */
export function erreurSociete(nom: string, existantes: readonly SocieteRole[]): string | null {
  if (!nom.trim()) return 'Le nom de la société est obligatoire.'
  if (societeNommee(nom, existantes)) return 'Cette société est déjà dans la liste.'
  return null
}

/** « Montage Élec · Méca », vide sans montage. */
export function libelleMontages(montages: readonly MetierMontage[]): string {
  const dans = METIERS_MONTAGE.filter((m) => montages.includes(m))
  return dans.length ? `Montage ${dans.map((m) => LIBELLE_MONTAGE[m]).join(' · ')}` : ''
}

/** Le domaine de l'externe ajouté à sa société, s'il n'y est pas (et n'est pas public). */
function avecDomaine(s: SocieteRole, email: string): SocieteRole {
  const d = domaineSociete(email)
  return d && !s.domaines.includes(d) ? { ...s, domaines: [...s.domaines, d] } : s
}

/**
 * Un externe ajouté (ou remplacé, même adresse). Sa société, si elle n'est pas
 * encore dans la liste, y entre avec le domaine de son adresse ; sinon ce
 * domaine s'ajoute à ceux de la société.
 */
export function avecExterne(roles: Roles, e: ExterneRole): Roles {
  const externes = [...roles.externes.filter((x) => x.email !== e.email), e]
  if (!e.societe.trim()) return { ...roles, externes }
  const connue = societeNommee(e.societe, roles.societes)
  const societes = connue
    ? roles.societes.map((s) => (s === connue ? avecDomaine(s, e.email) : s))
    : [...roles.societes, avecDomaine({ nom: e.societe.trim(), domaines: [], montages: [], ajoutePar: e.ajoutePar, le: e.le }, e.email)]
  return { ...roles, externes, societes }
}

/** Rattache un externe à une société de la liste (son nom écrit tel que la liste l'écrit). */
export function rattacherExterne(roles: Roles, email: string, nomSociete: string): Roles {
  const societe = societeNommee(nomSociete, roles.societes)
  const nom = societe?.nom ?? nomSociete.trim()
  return {
    ...roles,
    externes: roles.externes.map((x) => (x.email === email ? { ...x, societe: nom } : x)),
    societes: societe ? roles.societes.map((s) => (s === societe ? avecDomaine(s, email) : s)) : roles.societes,
  }
}

/** Une société retirée : ses externes gardent son nom, hors liste (rien n'est perdu). */
export function sansSociete(roles: Roles, nom: string): Roles {
  return { ...roles, societes: roles.societes.filter((s) => cleNom(s.nom) !== cleNom(nom)) }
}

/** Les montages d'une société, remplacés. */
export function avecMontages(roles: Roles, nom: string, montages: readonly MetierMontage[]): Roles {
  const garde = METIERS_MONTAGE.filter((m) => montages.includes(m))
  return { ...roles, societes: roles.societes.map((s) => (cleNom(s.nom) === cleNom(nom) ? { ...s, montages: garde } : s)) }
}

/** Le logo d'une société, remplacé ; `null` le retire (le fichier reste dans la bibliothèque). */
export function avecLogo(roles: Roles, nom: string, logo: string | null): Roles {
  return {
    ...roles,
    societes: roles.societes.map((s) => {
      if (cleNom(s.nom) !== cleNom(nom)) return s
      const { logo: _ancien, ...reste } = s
      return logo ? { ...reste, logo } : reste
    }),
  }
}

/** La société de la liste qui porte ce nom, avec son logo (affichage hors du panneau). */
export function logoDeSociete(nom: string, societes: readonly SocieteRole[]): string | null {
  return societeNommee(nom, societes)?.logo ?? null
}

export interface SocieteHorsListe {
  /** Le nom tel que le premier externe l'écrit. */
  nom: string
  externes: number
}

/**
 * Les sociétés écrites sur des externes mais absentes de la liste (externes
 * ajoutés avant le 29/10, ou société retirée) : le panneau propose de les ajouter.
 */
export function societesHorsListe(roles: Pick<Roles, 'externes' | 'societes'>): SocieteHorsListe[] {
  const parCle = new Map<string, SocieteHorsListe>()
  for (const e of roles.externes) {
    const cle = cleNom(e.societe)
    if (!cle || societeNommee(e.societe, roles.societes)) continue
    const s = parCle.get(cle) ?? { nom: e.societe.trim(), externes: 0 }
    s.externes += 1
    parCle.set(cle, s)
  }
  return [...parCle.values()].sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
}

/** La société hors liste ajoutée, avec les domaines de ses externes. */
export function ajouterSocieteHorsListe(roles: Roles, nom: string, par: string, le: string): Roles {
  if (societeNommee(nom, roles.societes)) return roles
  const cle = cleNom(nom)
  let societe: SocieteRole = { nom: nom.trim(), domaines: [], montages: [], ajoutePar: par, le }
  for (const e of roles.externes) if (cleNom(e.societe) === cle) societe = avecDomaine(societe, e.email)
  return { ...roles, societes: [...roles.societes, societe] }
}

export interface ExterneConnu {
  externe: ExterneRole
  /** Sa société dans la liste ; null : société hors liste ou non renseignée. */
  societe: SocieteRole | null
}

/**
 * L'externe derrière un compte invité. `adresse` : celle du compte, déjà
 * remise en clair (`x_vinci.com#EXT#@…` → `x@vinci.com`, adresseNormalisee).
 */
export function externeDuCompte(adresse: string, roles: Pick<Roles, 'externes' | 'societes'>): ExterneConnu | null {
  const a = adresse.trim().toLowerCase()
  const externe = a ? roles.externes.find((e) => e.email === a) : undefined
  return externe ? { externe, societe: societeDeLExterne(externe, roles.societes) } : null
}

/** Le dernier passage d'un externe dans l'appli (bloc invitesVus), ou null. */
export function venueDeLExterne(e: Pick<ExterneRole, 'email'>, vus: readonly InviteVu[]): InviteVu | null {
  const empreinte = sha256Hex(e.email.trim().toLowerCase())
  return vus.find((v) => v.empreinteAdresse === empreinte) ?? null
}

// ─── Comptes invités vus (appareils partagés, comptes secondaires) ───
//
// Un compte hors @sartorius.com qui ouvre l'appli se signale (une fois par jour
// et par appareil) dans le bloc `invitesVus` de la ligne __global__ : le
// panneau le propose alors en « Son compte », sans demander l'identifiant à
// l'IT. Gardés : l'Object ID, le nom affiché, l'empreinte SHA-256 de l'adresse
// (relecture de l'auteur) — JAMAIS l'adresse elle-même (souvent personnelle).

export const CLE_BLOC_INVITES_VUS = 'invitesVus'
/** Au-delà, les plus anciens sont oubliés. */
export const MAX_INVITES_VUS = 40

export interface InviteVu {
  objectId: string
  nom: string
  empreinteAdresse: string
  /** Date ISO du dernier passage. */
  le: string
}

/** Lecture tolérante du bloc, du plus récent au plus ancien. */
export function lireInvitesVus(bloc: unknown): InviteVu[] {
  const liste = estObjet(bloc) && Array.isArray(bloc.comptes) ? bloc.comptes : []
  const res: InviteVu[] = []
  for (const x of liste) {
    if (!estObjet(x)) continue
    const objectId = adresse(x.objectId)
    if (!objectId || res.some((r) => r.objectId === objectId)) continue
    res.push({ objectId, nom: texte(x.nom), empreinteAdresse: adresse(x.empreinteAdresse), le: texte(x.le) })
  }
  return res.sort((a, b) => b.le.localeCompare(a.le))
}

/** Le bloc après le passage de ce compte (remis en tête, daté). */
export function noterInviteVu(bloc: unknown, vu: InviteVu): { comptes: InviteVu[] } {
  const autres = lireInvitesVus(bloc).filter((x) => x.objectId !== vu.objectId.toLowerCase())
  return { comptes: [{ ...vu, objectId: vu.objectId.toLowerCase() }, ...autres].slice(0, MAX_INVITES_VUS) }
}

/**
 * Les comptes vus qui ne sont encore ni un appareil partagé, ni un compte
 * secondaire, ni un externe du panneau (reconnu à son adresse, 29/10).
 */
export function invitesAAttribuer(vus: readonly InviteVu[], roles: Pick<Roles, 'appareils' | 'secondaires' | 'externes'>): InviteVu[] {
  const pris = new Set([...roles.appareils.map((a) => a.objectId), ...roles.secondaires.map((s) => s.objectId)])
  const externes = new Set(roles.externes.map((e) => sha256Hex(e.email.trim().toLowerCase())))
  return vus.filter((v) => !pris.has(v.objectId) && !externes.has(v.empreinteAdresse))
}

/** Vrai pour un compte hors du domaine Sartorius (compte invité). */
export function estCompteInvite(adresse: string): boolean {
  const a = adresse.trim().toLowerCase()
  return a.includes('#ext#') || (a.includes('@') && !a.endsWith('@sartorius.com'))
}

/** « 7a2e…91b4 » : un identifiant lisible d'un coup d'œil, sans tout l'Object ID. */
export function identifiantCourt(objectId: string): string {
  const id = objectId.trim()
  return id.length > 10 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id
}
